import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertTriangle, ShieldAlert } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail: string;
  onDeleted: () => void;
}

export function DeleteAccountDialog({
  open,
  onOpenChange,
  userEmail,
  onDeleted,
}: DeleteAccountDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const { profile } = useAuth();

  const { data: pendingEscrowCount = 0 } = useQuery({
    queryKey: ['pending-escrow-count', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;
      const { count } = await supabase
        .from('escrow_payments')
        .select('id', { count: 'exact', head: true })
        .or(`payer_id.eq.${profile.id},payee_id.eq.${profile.id}`)
        .in('escrow_status', ['held', 'disputed']);
      return count || 0;
    },
    enabled: open && !!profile?.id,
  });

  const hasPendingEscrow = pendingEscrowCount > 0;
  const isConfirmed = confirmText.toLowerCase() === 'xóa tài khoản';

  const handleDelete = async () => {
    if (!isConfirmed || hasPendingEscrow) return;

    setIsDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Phiên đăng nhập đã hết hạn');
        return;
      }

      const response = await supabase.functions.invoke('delete-user-account', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Không thể xóa tài khoản');
      }

      const responseData = response.data;
      if (responseData?.error) {
        throw new Error(responseData.error);
      }

      toast.success('Tài khoản đã được xóa thành công');
      onDeleted();
    } catch (error: any) {
      console.error('Delete account error:', error);
      toast.error(error.message || 'Có lỗi xảy ra khi xóa tài khoản');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            <AlertDialogTitle>Xóa tài khoản vĩnh viễn</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-3 text-left">
            <p>
              Bạn đang yêu cầu xóa tài khoản <strong>{userEmail}</strong>.
            </p>

            {hasPendingEscrow && (
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 space-y-2">
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <ShieldAlert className="w-4 h-4 shrink-0" />
                  <p className="font-medium text-sm">Không thể xóa tài khoản</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Bạn đang có <strong>{pendingEscrowCount}</strong> giao dịch tài chính chưa hoàn tất 
                  (đang giữ hoặc đang tranh chấp). Vui lòng chờ các trận đấu/hoạt động kết thúc 
                  hoặc giải quyết xong tranh chấp trước khi xóa tài khoản.
                </p>
              </div>
            )}

            {!hasPendingEscrow && (
              <>
                <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20 space-y-2">
                  <p className="font-medium text-destructive text-sm">Hành động này không thể hoàn tác!</p>
                  <ul className="text-xs space-y-1 text-muted-foreground">
                    <li>• Tất cả dữ liệu cá nhân sẽ bị xóa</li>
                    <li>• Lịch sử trận đấu và xếp hạng sẽ bị xóa</li>
                    <li>• Các CLB bạn sở hữu sẽ bị xóa</li>
                    <li>• Các sân bạn đăng ký sẽ bị xóa</li>
                    <li>• Tin nhắn và thông báo sẽ bị xóa</li>
                  </ul>
                </div>
                <div className="space-y-2 pt-2">
                  <Label htmlFor="confirm-delete" className="text-sm">
                    Nhập <strong className="text-destructive">xóa tài khoản</strong> để xác nhận:
                  </Label>
                  <Input
                    id="confirm-delete"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="xóa tài khoản"
                    className="border-destructive/30 focus:border-destructive"
                    disabled={isDeleting}
                  />
                </div>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Hủy</AlertDialogCancel>
          {!hasPendingEscrow && (
            <AlertDialogAction
              onClick={handleDelete}
              disabled={!isConfirmed || isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Đang xóa...
                </>
              ) : (
                'Xóa tài khoản'
              )}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
